// useJobPoll — the ONE async-job polling loop for every Solution Studio panel (P0.4).
//
// Replaces the three hand-rolled setInterval(2500) copies in DesignPanel / DiagramsPanel /
// GenerationPanel, and fixes what they got wrong:
//   - RESUME ON MOUNT: `Tabs keepMounted={false}` unmounts a panel on tab switch, and the job object
//     was panel-local state — a minutes-long Opus build lost all progress visibility until the lock
//     cleared. The hook derives the active job from project.lockedBy (the lock holder IS the jobId)
//     or project.latestJobId and resumes polling; a terminal job re-renders its persisted row (result,
//     error, cost, steps) so a reload/tab switch never erases why a job succeeded or failed.
//   - BACKOFF: the poll cadence relaxes from 2.5s toward 15s on a long run instead of hammering.
//   - STALLED DETECTION: a job stuck in PENDING (e.g. the state-machine ARN unset — the UI itself
//     warns about that case) flips `stalled` instead of polling forever with no signal.
//   - The terminal result stays rendered until the engineer dismisses it — a background project
//     refresh no longer wipes the per-pass cost / revision diff / failure error.
//
// Panels with different job identities share one project-level `latestJobId` pointer, so `match`
// scopes the resume: DesignPanel matches kind:'design' + stage:'design', DiagramsPanel
// kind:'design' + stage:'flows', Build/Test their kinds.

import { useState, useRef, useCallback, useEffect } from 'react';
import { pollJob } from '../api_scripts/solnStudioClient';

export const TERMINAL = ['SUCCEEDED', 'FAILED'];

const BASE_INTERVAL_MS = 2500;
const MAX_INTERVAL_MS = 15000;
const BACKOFF_AFTER_POLLS = 24; // ~1 minute at the base cadence before relaxing
const STALLED_PENDING_MS = 90_000; // PENDING this long ⇒ the machine almost certainly never started

export default function useJobPoll({ project, getApiToken, match, onTerminal }) {
  const [job, setJob] = useState(null);
  const [stalled, setStalled] = useState(false);

  const timerRef = useRef(null);
  const stateRef = useRef({ jobId: null, polls: 0, startedAt: 0 });
  // Jobs we already resumed (or the user dismissed) — a background project refresh must not
  // re-surface them. Ref, not state: it never needs to re-render anything.
  const seenRef = useRef(new Set());
  // Keep the latest callbacks without re-arming timers on every render.
  const matchRef = useRef(match);
  matchRef.current = match;
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  const stop = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const tick = useCallback(async () => {
    const s = stateRef.current;
    if (!s.jobId) return;
    let j;
    try {
      j = await pollJob(s.jobId, getApiToken);
    } catch {
      // Transient poll error (network blip, token refresh) — keep trying at the relaxed cadence
      // instead of killing the loop and losing the job.
      timerRef.current = setTimeout(tick, MAX_INTERVAL_MS);
      return;
    }
    setJob(j);
    s.polls += 1;
    if (TERMINAL.includes(j.status)) {
      stop();
      setStalled(false);
      if (onTerminalRef.current) onTerminalRef.current(j);
      return;
    }
    setStalled(j.status === 'PENDING' && Date.now() - s.startedAt > STALLED_PENDING_MS);
    const over = s.polls - BACKOFF_AFTER_POLLS;
    const interval = over > 0 ? Math.min(MAX_INTERVAL_MS, BASE_INTERVAL_MS * 1.5 ** over) : BASE_INTERVAL_MS;
    timerRef.current = setTimeout(tick, interval);
  }, [getApiToken, stop]);

  /** Begin polling a just-submitted job. `seed` renders immediately (estimate etc.) pending the first poll. */
  const start = useCallback((jobId, seed) => {
    stop();
    seenRef.current.add(jobId);
    stateRef.current = { jobId, polls: 0, startedAt: Date.now() };
    setStalled(false);
    setJob(seed || { jobId, status: 'PENDING' });
    timerRef.current = setTimeout(tick, BASE_INTERVAL_MS);
  }, [stop, tick]);

  /** Clear the rendered job (the engineer read the result). It will not be resurrected by a refresh. */
  const dismiss = useCallback(() => {
    if (stateRef.current.jobId) seenRef.current.add(stateRef.current.jobId);
    stop();
    stateRef.current = { jobId: null, polls: 0, startedAt: 0 };
    setJob(null);
    setStalled(false);
  }, [stop]);

  // Resume on mount / project refresh: the lock holder is the running jobId; latestJobId covers a job
  // that already finished. Each candidate is examined once; onTerminal deliberately does NOT fire for
  // a job that was already terminal when resumed (its side effects — deliverable refresh etc. — ran
  // when it actually finished).
  useEffect(() => {
    const lockJob = project?.lockedBy && String(project.lockedBy).startsWith('job_') ? project.lockedBy : null;
    const candidate = lockJob || project?.latestJobId;
    if (!candidate || stateRef.current.jobId || seenRef.current.has(candidate)) return;
    seenRef.current.add(candidate);
    let cancelled = false;
    (async () => {
      let j;
      try {
        j = await pollJob(candidate, getApiToken);
      } catch {
        return; // job row may have TTL'd out — nothing to resume
      }
      if (cancelled || !j) return;
      if (matchRef.current && !matchRef.current(j)) return; // someone else's job (another panel/kind)
      setJob(j);
      if (!TERMINAL.includes(j.status)) {
        stateRef.current = { jobId: candidate, polls: 0, startedAt: j.createdAt || Date.now() };
        timerRef.current = setTimeout(tick, BASE_INTERVAL_MS);
      }
    })();
    return () => { cancelled = true; };
  }, [project?.lockedBy, project?.latestJobId, getApiToken, tick]);

  useEffect(() => stop, [stop]); // never leak a timer on unmount

  return {
    job,
    stalled,
    running: !!job && !TERMINAL.includes(job.status),
    start,
    dismiss,
  };
}
